import {$getRoot, type LexicalEditor} from 'lexical';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {setPersistedHeadingCollapsed} from './HeadingCollapsePersistence';

const getHeadingLevelFromElement = (element: HTMLElement): number | null => {
  const match = element.tagName.match(/^H([1-6])$/);
  return match ? Number(match[1]) : null;
};

export const getLexicalEditorRootElement = (element: HTMLElement): HTMLElement | null =>
  element.closest('[data-lexical-editor]');

export const getTopLevelEditorChild = (
  element: HTMLElement,
  rootElement: HTMLElement
): HTMLElement | null => {
  let current: HTMLElement | null = element;
  while (current?.parentElement && current.parentElement !== rootElement) {
    current = current.parentElement;
  }

  return current?.parentElement === rootElement ? current : null;
};

/**
 * Returns collapsed heading elements that hide `sectionHeadingElement` or are
 * the section heading itself when collapsed.
 */
export const findCollapsedHeadingsForSection = (
  sectionHeadingElement: HTMLElement,
  {includeTargetHeading = true}: {includeTargetHeading?: boolean} = {}
): HTMLElement[] => {
  const rootElement = getLexicalEditorRootElement(sectionHeadingElement);
  if (!rootElement) return [];

  const topLevelHeading = getTopLevelEditorChild(sectionHeadingElement, rootElement);
  if (!topLevelHeading) return [];

  const children = Array.from(rootElement.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
  const targetIndex = children.indexOf(topLevelHeading);
  if (targetIndex === -1) return [];

  const endIndex = includeTargetHeading ? targetIndex + 1 : targetIndex;
  const collapsedStack: Array<{element: HTMLElement; level: number}> = [];

  for (const child of children.slice(0, endIndex)) {
    const headingLevel = getHeadingLevelFromElement(child);
    if (!headingLevel) continue;

    while (
      collapsedStack.length > 0 &&
      collapsedStack[collapsedStack.length - 1].level >= headingLevel
    ) {
      collapsedStack.pop();
    }

    if (child.getAttribute('data-collapsed') === 'true') {
      collapsedStack.push({element: child, level: headingLevel});
    }
  }

  return collapsedStack.map(({element}) => element);
};

const findHeadingNodeByHeadingId = (headingId: string): EnhancedHeadingNode | null => {
  for (const child of $getRoot().getChildren()) {
    if (child instanceof EnhancedHeadingNode && child.getHeadingId() === headingId) {
      return child;
    }
  }
  return null;
};

export const expandCollapsedSectionsForHeading = ({
  editor,
  headingElement,
  collabId,
}: {
  editor: LexicalEditor;
  headingElement: HTMLElement;
  collabId?: string;
}): boolean => {
  const collapsedHeadingIds = findCollapsedHeadingsForSection(headingElement)
    .map((heading) => heading.getAttribute('data-heading-id'))
    .filter((headingId): headingId is string => Boolean(headingId));

  if (!collapsedHeadingIds.length) return false;

  editor.update(() => {
    collapsedHeadingIds.forEach((headingId) => {
      const node = findHeadingNodeByHeadingId(headingId);
      if (!node || !node.getCollapsed()) return;

      node.setCollapsed(false);
      if (collabId) {
        setPersistedHeadingCollapsed(collabId, headingId, false);
      }
      node.markDirty();
    });
  });

  return true;
};
